/**
 * Minimal Supabase client over plain fetch — auth (magic link), REST and
 * storage. All Supabase knowledge lives here and in lib/eventsService.ts.
 *
 * The publishable key is safe to ship: row-level security in the database
 * decides who may write, not secrecy of the key.
 */

export const SUPABASE_URL = 'https://wezwdqsgamrtjcdgptys.supabase.co'
export const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_y3gTki2gqJHq2FALCN6Mkw_ocbqmQ1S'

export const isSupabaseConfigured =
  SUPABASE_URL.length > 0 && SUPABASE_PUBLISHABLE_KEY.length > 0

export interface AdminSession {
  accessToken: string
  refreshToken: string
  /** Unix seconds */
  expiresAt: number
  email: string | null
}

interface TokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  expires_at?: number
  user?: { email?: string }
}

const SESSION_STORAGE_KEY = 'pines-admin-session'

export class SupabaseError extends Error {
  status: number
  code: string | null

  constructor(message: string, status: number, code: string | null = null) {
    super(message)
    this.name = 'SupabaseError'
    this.status = status
    this.code = code
  }
}

export function errorMessage(error: unknown): string {
  if (error instanceof SupabaseError && error.status === 0) {
    return 'Couldn’t reach the server. Check your connection and try again.'
  }
  if (error instanceof Error && error.message) return error.message
  return 'Something went wrong. Please try again.'
}

/* ------------------------------------------------------------------ */
/* Session storage                                                     */
/* ------------------------------------------------------------------ */

export function readSession(): AdminSession | null {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as AdminSession) : null
  } catch {
    return null
  }
}

export function writeSession(session: AdminSession): void {
  window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
}

export function clearSession(): void {
  window.localStorage.removeItem(SESSION_STORAGE_KEY)
}

function sessionFromTokens(tokens: TokenResponse, email: string | null): AdminSession {
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    expiresAt: tokens.expires_at ?? Math.floor(Date.now() / 1000) + tokens.expires_in,
    email,
  }
}

let refreshInFlight: Promise<AdminSession | null> | null = null

function refreshSession(current: AdminSession): Promise<AdminSession | null> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const response = await fetch(
          `${SUPABASE_URL}/auth/v1/token?grant_type=refresh_token`,
          {
            method: 'POST',
            headers: {
              apikey: SUPABASE_PUBLISHABLE_KEY,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: current.refreshToken }),
          },
        )
        if (!response.ok) {
          clearSession()
          return null
        }
        const tokens = (await response.json()) as TokenResponse
        const next = sessionFromTokens(tokens, tokens.user?.email ?? current.email)
        writeSession(next)
        return next
      } catch {
        // Network failure: keep the session so a retry can succeed later.
        return current
      }
    })().finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  const session = readSession()
  if (!session) return null
  const secondsLeft = session.expiresAt - Math.floor(Date.now() / 1000)
  if (forceRefresh || secondsLeft < 60) {
    const next = await refreshSession(session)
    return next?.accessToken ?? null
  }
  return session.accessToken
}

/* ------------------------------------------------------------------ */
/* Requests                                                            */
/* ------------------------------------------------------------------ */

async function toSupabaseError(response: Response): Promise<SupabaseError> {
  let message = `Request failed (${response.status})`
  let code: string | null = null
  try {
    const body = (await response.json()) as Record<string, unknown>
    const candidate = body.msg ?? body.message ?? body.error_description ?? body.error
    if (typeof candidate === 'string') message = candidate
    const candidateCode = body.error_code ?? body.code
    if (typeof candidateCode === 'string') code = candidateCode
  } catch {
    // Non-JSON error body — keep the generic message.
  }
  return new SupabaseError(message, response.status, code)
}

export async function supabaseFetch(
  path: string,
  init: RequestInit = {},
  options: { auth?: boolean } = {},
): Promise<Response> {
  const run = async (forceRefresh: boolean) => {
    const headers = new Headers(init.headers)
    headers.set('apikey', SUPABASE_PUBLISHABLE_KEY)
    if (options.auth) {
      const token = await getAccessToken(forceRefresh)
      if (!token) {
        throw new SupabaseError(
          'Your sign-in has ended. Please sign in again.',
          401,
          'session_expired',
        )
      }
      headers.set('Authorization', `Bearer ${token}`)
    }
    try {
      return await fetch(`${SUPABASE_URL}${path}`, { ...init, headers })
    } catch {
      throw new SupabaseError('Network error', 0, 'network')
    }
  }

  let response = await run(false)
  if (options.auth && response.status === 401) response = await run(true)
  if (!response.ok) throw await toSupabaseError(response)
  return response
}

/* ------------------------------------------------------------------ */
/* Auth                                                                */
/* ------------------------------------------------------------------ */

export async function sendMagicLink(email: string, redirectTo: string): Promise<void> {
  await supabaseFetch(`/auth/v1/otp?redirect_to=${encodeURIComponent(redirectTo)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // create_user: false — only people already added as users get a link.
    body: JSON.stringify({ email, create_user: false }),
  })
}

/**
 * The magic link returns to /admin with tokens (or an error) in the URL hash.
 * Store the session and strip the hash so tokens never linger in the address bar.
 */
export function consumeSessionFromUrl(): { error: string | null } {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : ''
  if (!hash) return { error: null }

  const params = new URLSearchParams(hash)
  const error = params.get('error_description') ?? params.get('error')
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  if (!error && !accessToken) return { error: null }

  window.history.replaceState({}, '', window.location.pathname + window.location.search)

  if (error) return { error }
  if (!accessToken || !refreshToken) return { error: 'invalid link' }

  const expiresIn = Number(params.get('expires_in') ?? 3600)
  const expiresAt =
    Number(params.get('expires_at')) || Math.floor(Date.now() / 1000) + expiresIn
  writeSession({ accessToken, refreshToken, expiresAt, email: null })
  return { error: null }
}

export async function fetchCurrentEmail(): Promise<string | null> {
  const response = await supabaseFetch('/auth/v1/user', {}, { auth: true })
  const user = (await response.json()) as { email?: string }
  return user.email ?? null
}

export async function signOutSession(): Promise<void> {
  try {
    await supabaseFetch('/auth/v1/logout', { method: 'POST' }, { auth: true })
  } catch {
    // Signing out locally is what matters.
  } finally {
    clearSession()
  }
}
